import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import Button from "../../shared/button/Button";
import CustomInputText from "../../shared/forms/input/CustomInputText";
import { useFormik } from "formik";
import * as Yup from "yup";
import CustomSelect from "../../shared/forms/select/CustomSelect";
import { membershipGroupTypes } from "../../../models/selectOptions";
import { mapFieldToSelectValue, mapUserCfuData } from "../../../utils/mapping";
import { User, UserDetail } from "../../../data/interfaces";

// -----------------------------------------------------------------
// I n t e r f a c e s
// -----------------------------------------------------------------
interface Props {
  user?: UserDetail | User | any | null;
  pending?: boolean;
  onSubmit: any;
  onCancel?: any;
  showTitle?: boolean;
  showDesc?: boolean;
  submitLabel?: string;
}

const CfuDataForm = ({
                       user,
                       pending,
                       showTitle = true,
                       showDesc = true,
                       submitLabel,
                       onSubmit,
                       onCancel,
                       ...props
                     }: Props) => {
  const mapUserToForm = (user: any) => {
    return {
      cfu_info_fiscal_code: user?.cfu_info?.fiscal_code,
      cfu_info_membership_group: mapFieldToSelectValue(
        user?.cfu_info?.membership_group,
        membershipGroupTypes,
      ),
      cfu_info_membership_group_location: user?.cfu_info?.membership_group_location,
      cfu_info_subscription_number: user?.cfu_info?.subscription_number,
      privacy_policy_consent: user?.privacy_policy_consent || false,
    };
  };

  // -----------------------------------------------------------------
  // L o c a l   v a r s
  // -----------------------------------------------------------------
  // Access to i18n instance that you may use to translate your content.
  const { t } = useTranslation();

  // -----------------------------------------------------------------
  // f o r m
  // -----------------------------------------------------------------
  // Formik Initialisation data
  const form = useFormik({
    initialValues: !!user
      ? mapUserToForm(user)
      : {
        cfu_info_fiscal_code: "",
        cfu_info_membership_group: "",
        cfu_info_membership_group_location: "",
        cfu_info_subscription_number: "",
        privacy_policy_consent: false,
      },
    enableReinitialize: true,
    validationSchema: Yup.object().shape({
      cfu_info_fiscal_code: Yup.string().required("Field is required"),
      cfu_info_membership_group: Yup.mixed().required("Field is required!"),
      cfu_info_membership_group_location: Yup.string().nullable().required("Field is required"),
      cfu_info_subscription_number: Yup.string().required("Field is required"),
      privacy_policy_consent: Yup.boolean().equals([true]),
    }),
    onSubmit: (values: any) => {
      // if (!!form.dirty) {
      onSubmit(mapUserCfuData(values));
      // }
    },
  });

  // -----------------------------------------------------------------
  // N a v i g a t i o n   v a r s
  // -----------------------------------------------------------------

  // -----------------------------------------------------------------
  // S t a t e
  // -----------------------------------------------------------------

  // -----------------------------------------------------------------
  // R e f s  (DOM)
  // -----------------------------------------------------------------

  // -----------------------------------------------------------------
  // u s e S e l e c t o r   m e t h o d s  (redux)
  // -----------------------------------------------------------------

  // -----------------------------------------------------------------
  // W o r k i n g   m e t h o d s
  // -----------------------------------------------------------------

  // -----------------------------------------------------------------
  // R e n d e r   m e t h o d s
  // -----------------------------------------------------------------

  // -----------------------------------------------------------------
  // L i f e c y c l e
  // -----------------------------------------------------------------
  /**
   * This method is called the first time the component is mounted
   * @function
   */
  const init = (): void => {
    // init component
    console.log("[CompleteProfileForm] init");
  };

  /**
   * This method is called when the component is unmounted
   * @function
   */
  const destroy = (): void => {
    // destroy component
    console.log("[CompleteProfileForm] destroy");
  };

  // -----------------------------------------------------------------
  // u s e E f f e c t   m e t h o d s
  // -----------------------------------------------------------------
  /**
   * This hook is called once when the component is mounted
   */
  useEffect(() => {
    init();
    return () => {
      destroy();
    };
  }, []);

  // -----------------------------------------------------------------
  // T e m p l a t e
  // -----------------------------------------------------------------
  return (
    <div className="profile-form personal-data">
      {!!showTitle && (
        <h1 className="h4 text-gray-900 mb-2">{t("pages.profile.forms.cfu_data.title")}</h1>
      )}
      {!!showDesc && <p className="desc mb-4">{t("pages.profile.forms.cfu_data.description")}</p>}
      <form onSubmit={form.handleSubmit} className="user">
        <div className="form-group row">
          <div className="col-xs-12 col-sm-6 mb-3 mb-sm-0">
            <CustomInputText
              label={t("forms.fiscal_code")}
              name="cfu_info_fiscal_code"
              id="cfu_info_fiscal_code"
              onChange={form.handleChange}
              onBlur={form.handleBlur}
              value={form.values.cfu_info_fiscal_code}
              errors={form.errors.cfu_info_fiscal_code}
              touched={form.touched.cfu_info_fiscal_code}
            />
          </div>
          <div className="col-xs-12 col-sm-6">
            <CustomInputText
              label={t("forms.subscription_number")}
              name="cfu_info_subscription_number"
              id="cfu_info_subscription_number"
              onChange={form.handleChange}
              onBlur={form.handleBlur}
              value={form.values.cfu_info_subscription_number}
              errors={form.errors.cfu_info_subscription_number}
              touched={form.touched.cfu_info_subscription_number}
            />
          </div>
        </div>
        <div className="form-group">
          <CustomSelect
            id="cfu_info_membership_group"
            name="cfu_info_membership_group"
            label={t("forms.membership_group")}
            options={membershipGroupTypes}
            value={form.values.cfu_info_membership_group}
            onChange={(event: any) => {
              form.setFieldValue("cfu_info_membership_group", event, true);
            }}
            onBlur={(ev: any) => {
              form.setFieldTouched("cfu_info_membership_group", true, true);
            }}
            errors={form.errors.cfu_info_membership_group}
            touched={form.touched.cfu_info_membership_group}
          />
        </div>
        <div className="form-group">
          <CustomInputText
            label={t("forms.membership_group_location")}
            name="cfu_info_membership_group_location"
            id="cfu_info_membership_group_location"
            onChange={form.handleChange}
            onBlur={form.handleBlur}
            value={form.values.cfu_info_membership_group_location}
            errors={form.errors.cfu_info_membership_group_location}
            touched={form.touched.cfu_info_membership_group_location}
          />
        </div>
        {/* <div className="form-group">
                    <PrivacyConsentCheckbox
                        disabled={user?.privacy_policy_consent}
                        form={form}
                        onBlur={form.handleBlur}
                        value={form.values.privacy_policy_consent}
                        errors={form.errors.privacy_policy_consent}
                        touched={form.touched.privacy_policy_consent}
                    />
                </div> */}

        <div className="actions">
          {!!onCancel && (
            <Button
              disabled={pending}
              loading={false}
              submit={false}
              onClick={onCancel}
              className="btn btn-default"
            >
              {t("forms.cancel")}
            </Button>
          )}
          <Button
            disabled={pending || !form.isValid}
            loading={pending}
            submit={true}
            className="btn btn-primary"
          >
            {!!submitLabel ? submitLabel : t("forms.submit")}
          </Button>
        </div>
      </form>
    </div>
  );
};
export default CfuDataForm;
